import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { Black25x26Comparativo } from "@/components/admin/Black25x26Comparativo";

export const Route = createFileRoute("/admin/black25-crm-direta")({
  head: () => ({
    meta: [
      { title: "Admin · Black em Dobro 2025: CRM x venda direta" },
      { name: "description", content: "Estudo da Black em Dobro 2025: vendas pelo CRM (listas) e vendas diretas, canais, perfis e lições para 2026." },
      { property: "og:title", content: "Admin · Black em Dobro 2025: CRM x venda direta" },
      { property: "og:description", content: "Vendas pelo CRM e vendas diretas na Black em Dobro 2025." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/black25-crm-direta">
      <Page />
    </AdminGate>
  ),
});

const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const pct = (v: number) => `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

/** Estudo "Lista principal x Entregas" (RD Station + Eduzz, 1ª fatura paga 07/10 a 03/11/2025). */
const TOTAL = { vendas: 697, fat: 1650084, ticket: 2367, unicas: 240, assin: 457 };

type Grupo = { nome: string; vendas: number; fat: number; ticket: number; unicas: number; assin: number; tipo: "crm" | "direta" };
const VISOES: Record<"geral" | "paga", { rotulo: string; nota: string; grupos: Grupo[] }> = {
  geral: {
    rotulo: "Todos os leads",
    nota: "Conta como CRM quem estava inscrito em alguma lista (essas pessoas foram trabalhadas pelas vendedoras). Venda direta = comprou sem estar em lista: base de alunos, e-mail e WhatsApp na abertura.",
    grupos: [
      { nome: "Lista principal (Black)", vendas: 433, fat: 1029555, ticket: 2378, unicas: 139, assin: 294, tipo: "crm" },
      { nome: "Lista das entregas (materiais)", vendas: 24, fat: 58040, ticket: 2418, unicas: 9, assin: 15, tipo: "crm" },
      { nome: "Fora das listas (direta/orgânica)", vendas: 240, fat: 562489, ticket: 2344, unicas: 92, assin: 148, tipo: "direta" },
    ],
  },
  paga: {
    rotulo: "Só leads de anúncio",
    nota: "Visão mais rígida: só conta como lista quem entrou por anúncio pago. Leads orgânicos da lista (e-mail, direto, social) passam para o grupo de fora.",
    grupos: [
      { nome: "Lista principal (anúncio)", vendas: 168, fat: 397344, ticket: 2365, unicas: 54, assin: 114, tipo: "crm" },
      { nome: "Lista das entregas (anúncio)", vendas: 24, fat: 58040, ticket: 2418, unicas: 9, assin: 15, tipo: "crm" },
      { nome: "Fora dos leads pagos", vendas: 505, fat: 1194699, ticket: 2366, unicas: 177, assin: 328, tipo: "direta" },
    ],
  },
};

const LISTAS = [
  ["Inscrições no RD", "2.037", "1.874"],
  ["Pessoas únicas", "1.787", "1.623"],
  ["Vindos de anúncio", "40,5%", "99,6%"],
  ["Compradores", "433", "24"],
  ["Conversão", "24,2%", "1,5%"],
  ["Faturamento por lead", "R$ 576", "R$ 36"],
  ["Vendas antes da abertura (até 26/10)", "114", "1"],
  ["Vendas no dia da abertura (27/10)", "75 (17,3%)", "5 (20,8%)"],
  ["Dias entre inscrição e compra (mediana)", "10", "6"],
  ["Pagamento", "Crédito 60,5% · Pix 20,6% · Boleto 18,9%", "Crédito 66,7% · Boleto/Pix 16,7%"],
  ["Parcelas médias na única", "3,5x", "8,0x"],
  ["Estudantes na lista", "15,5%", "43,4%"],
];

const PERFIL = [
  ["Fisioterapeuta", "1.497", "25,8%", "386"],
  ["Estudante último ano", "277", "16,2%", "45"],
  ["Ainda não é aluno", "918", "21,2%", "195"],
  ["Aluno de Pós", "330", "26,4%", "87"],
  ["Aluno da Star", "313", "32,6%", "102"],
  ["Aluno de Cursos Gratuitos", "150", "22,0%", "33"],
  ["Aluno de Certificações", "65", "21,5%", "14"],
];

const CANAIS = [
  ["Anúncio pago", "723", "23,2%", "168"],
  ["Tráfego direto", "394", "20,8%", "82"],
  ["E-mail", "246", "31,7%", "78"],
  ["Outros/desconhecido", "226", "20,4%", "46"],
  ["Social orgânico", "193", "29,0%", "56"],
  ["Busca orgânica", "5", "60,0%", "3"],
];

const SEMANAS = [
  ["06/10", "513", "29,6%"],
  ["13/10", "739", "22,5%"],
  ["20/10", "501", "21,2%"],
  ["27/10", "26", "34,6%"],
];

const CAMPANHAS = [
  ["[TR] [LEAD] [BLACKDAFEO] [2025]", "591", "140", "23,7%"],
  ["[TR] [LEAD] [TKY] [BLACKDAFEO] [2025]", "100", "22", "22,0%"],
  ["E-mail 1 lista de espera (base)", "40", "17", "42,5%"],
  ["E-mail 4 lista de espera (base)", "15", "6", "40,0%"],
  ["E-mail 6 lista de espera (base)", "17", "6", "35,3%"],
  ["E-mail venda “é amanhã” (base)", "16", "5", "31,2%"],
  ["E-mail 5 lista de espera (base)", "27", "8", "29,6%"],
  ["E-mail 2 lista de espera (base)", "40", "9", "22,5%"],
];

const LICOES = {
  replicar: [
    "Lista principal da Black: 24% de conversão e R$ 576 por lead. É o ativo que mais vende.",
    "E-mails para a base (lista de espera): melhor conversão da ação, até 42%. Disparar sequência completa.",
    "Alunos Star e Pós convertem mais que quem não é aluno (32,6% e 26,4%). Abordagem dedicada à base.",
    "Pré-venda forte: 114 vendas antes da abertura e 75 no dia 27/10. A abertura concentra ~17% das vendas.",
    "Venda direta (sem lista) fez 34% do faturamento: WhatsApp e e-mail na abertura para toda a base.",
  ],
  evitar: [
    "Lista das entregas (materiais gratuitos) converteu 1,5%: lead de material não é lead de Black.",
    "Estudantes converteram pouco (0,6% nas entregas, 16% na principal). Não priorizar no CRM.",
    "Captação quase parou após 27/10: quem entrou na última semana converteu 34,6%, então manter captação aberta vale a pena.",
  ],
};

function Page() {
  const [visao, setVisao] = useState<"geral" | "paga">("geral");
  const v = VISOES[visao];
  const crm = v.grupos.filter((g) => g.tipo === "crm");
  const dir = v.grupos.filter((g) => g.tipo === "direta");
  const soma = (gs: Grupo[], k: "vendas" | "fat") => gs.reduce((s, g) => s + g[k], 0);

  return (
    <div className="mx-auto max-w-6xl space-y-8 p-4 md:p-8">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Admin
      </Link>
      <header>
        <h1 className="text-2xl font-bold md:text-3xl">Black em Dobro · CRM, venda orgânica e venda direta</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {TOTAL.vendas} vendas · {brl(TOTAL.fat)} · ticket {brl(TOTAL.ticket)} · {TOTAL.unicas} únicas / {TOTAL.assin} assinaturas.
          Fonte: RD Station (inscrições) × Eduzz (1ª fatura paga, 07/10 a 03/11/2025), cruzamento por e-mail.
        </p>
      </header>

      <Secao titulo="2025 x 2026 · onde está o gargalo">
        <Black25x26Comparativo />
      </Secao>

      <section className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {(Object.keys(VISOES) as Array<"geral" | "paga">).map((k) => (
            <button
              key={k}
              onClick={() => setVisao(k)}
              className={`rounded-md border px-3 py-1.5 text-sm ${visao === k ? "bg-primary text-primary-foreground" : "bg-card"}`}
            >
              {VISOES[k].rotulo}
            </button>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Card titulo="Compradores que estavam na lista (CRM)" vendas={soma(crm, "vendas")} fat={soma(crm, "fat")} />
          <Card titulo="Compradores fora da lista (direta/orgânica)" vendas={soma(dir, "vendas")} fat={soma(dir, "fat")} />
        </div>
        <p className="text-xs text-muted-foreground">{v.nota}</p>
        <Tabela
          cab={["Grupo", "Vendas", "% vendas", "Faturamento", "Ticket", "Únicas / assin."]}
          linhas={v.grupos.map((g) => [
            g.nome,
            String(g.vendas),
            pct((g.vendas / TOTAL.vendas) * 100),
            brl(g.fat),
            brl(g.ticket),
            `${g.unicas} / ${g.assin}`,
          ])}
        />
        <p className="text-xs text-muted-foreground">
          Limites: quem comprou com outro e-mail cai em "venda direta". O estudo não registra qual vendedora fechou cada venda, então "CRM" significa
          "lead que estava no CRM", não necessariamente "fechada pela vendedora". Faturamento sem abater juros do parcelamento.
        </p>
      </section>

      <Secao titulo="Lista principal × lista das entregas">
        <Tabela cab={["", "Lista principal", "Entregas"]} linhas={LISTAS} />
      </Secao>

      <div className="grid gap-6 md:grid-cols-2">
        <Secao titulo="Perfil da lista principal">
          <Tabela cab={["Perfil", "Leads", "Conv.", "Compr."]} linhas={PERFIL} />
        </Secao>
        <Secao titulo="Canal de entrada (lista principal)">
          <Tabela cab={["Canal", "Leads", "Conv.", "Compr."]} linhas={CANAIS} />
        </Secao>
        <Secao titulo="Inscrições por semana (principal)">
          <Tabela cab={["Semana de", "Leads", "Conv."]} linhas={SEMANAS} />
        </Secao>
        <Secao titulo="Campanhas que mais venderam (principal)">
          <Tabela cab={["Campanha", "Leads", "Compr.", "Conv."]} linhas={CAMPANHAS} />
        </Secao>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Secao titulo="O que deu certo · replicar em 2026">
          <ul className="list-disc space-y-2 pl-5 text-sm">{LICOES.replicar.map((l) => <li key={l}>{l}</li>)}</ul>
        </Secao>
        <Secao titulo="O que não funcionou">
          <ul className="list-disc space-y-2 pl-5 text-sm">{LICOES.evitar.map((l) => <li key={l}>{l}</li>)}</ul>
        </Secao>
      </div>

      <p className="text-xs text-muted-foreground">
        Mais dados da ação (etapas, gastos, cupons, metas):{" "}
        <a href="/inteligencia-black25" className="underline">Inteligência Black 25</a>.
      </p>
    </div>
  );
}

function Card({ titulo, vendas, fat }: { titulo: string; vendas: number; fat: number }) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <p className="text-sm text-muted-foreground">{titulo}</p>
      <p className="mt-1 text-3xl font-bold">{vendas} vendas</p>
      <p className="text-sm">
        {pct((vendas / TOTAL.vendas) * 100)} das vendas · {brl(fat)} ({pct((fat / TOTAL.fat) * 100)} do faturamento)
      </p>
    </div>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <h2 className="font-semibold">{titulo}</h2>
      {children}
    </section>
  );
}

function Tabela({ cab, linhas }: { cab: string[]; linhas: string[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">{cab.map((c) => <th key={c} className="py-2 pr-3 font-medium">{c}</th>)}</tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => (
            <tr key={i} className="border-b last:border-0">{l.map((c, j) => <td key={j} className={`py-2 pr-3 ${j === 0 ? "font-medium" : ""}`}>{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
